import "./receptionTypes.js";
import { Box, Card, CardContent, Typography } from "@mui/material";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { status, TREATED_NO_SHOW_M } from "shared/src/appointment.mjs";
import { TreatedClientHeader } from "./TreatedClientHeader";
import { TreatedClient } from "./TreatedClient";
import { lastAppointmentStateChangeIn } from "./utils.js";

function treatedIn(appointment) {
  const treated = lastAppointmentStateChangeIn(appointment, status.TREATED);
  if (treated === Infinity) {
    return lastAppointmentStateChangeIn(appointment, status.FINISHED);
  }

  return treated;
}

/**
 * A component to display a list of treated clients
 *
 * @typedef {object} TreatedClientListProps
 * @property {Array<Appointment} appointments
 * @property {function} setReservationDialogOptions Set the options for the reservation dialog
 * @property {function} setIsHovered Setter function for the isHovered state
 * @property {string} hoverOverId
 * @property {function} setHoverOverId Setter function for the hoverOverId state
 * @param {number} order_from_h Number of hours in advance a follow-up appointment can be booked
 *
 * @param {TreatedClientListProps} props
 * @returns {ReactElement}
 */
export const TreatedClientList = ({
  appointments,
  setReservationDialogOptions,
  setIsHovered,
  hoverOverId,
  setHoverOverId,
  order_from_h,
}) => {
  const [treatedClients, setTreatedClients] = useState(appointments);
  const { t } = useTranslation();

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  useEffect(() => {
    setTreatedClients(
      appointments
        .filter(
          (appointment) =>
            appointment.parent_id == null &&
            appointment.adhoc_type !== "CHAIR" &&
            (appointment.status_id === status.TREATED ||
              appointment.status_id === status.FINISHED) &&
            appointment.followup_id == null &&
            treatedIn(appointment) < TREATED_NO_SHOW_M,
        )
        .sort(
          (a, b) =>
            new Date(
              a.audit.data.findLast(
                (change) =>
                  change?.changed_fields?.status_id === String(status.TREATED),
              )?.created_at,
            ).getTime() -
            new Date(
              b.audit.data.findLast(
                (change) =>
                  change?.changed_fields?.status_id === String(status.TREATED),
              )?.created_at,
            ).getTime(),
        ),
    );
  }, [appointments]);

  return (
    <Card onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h2" gutterBottom>
            {t("reception.treated_clients")}
          </Typography>
        </Box>

        <TreatedClientHeader />

        {treatedClients.map((appointment) => (
          <TreatedClient
            key={appointment.id}
            appointment={appointment}
            setReservationDialogOptions={setReservationDialogOptions}
            hoverOverId={hoverOverId}
            setHoverOverId={setHoverOverId}
            order_from_h={order_from_h}
          />
        ))}
      </CardContent>
    </Card>
  );
};
